import {
  minutes,
  units as makeUnits,
  type ISODateTime,
  type Minutes,
  type TaskId,
} from '../domain/branded.js';
import type { Calendar } from '../domain/calendar.js';
import type { Project } from '../domain/project.js';
import type { Resource } from '../domain/resource.js';
import type { Task } from '../domain/task.js';
import {
  addWorkingMinutes,
  intersectWorkingCalendars,
  resolveCalendar,
  snapToNextWorkingTime,
  workingMinutesBetween,
  type ResolvedCalendar,
} from './calendar-engine.js';
import { recalculateEffort } from './scheduling-task-type.js';

/**
 * 「工期 ↔ 開始日 ↔ 完成日」互推（MS Project 語義）。
 *
 * 這是**純函式**層：只計算「使用者改了其中一項之後，這顆任務的欄位應該長什麼樣」，
 * 不碰 store、不碰歷史、不重排整份專案（那是 `schedule()` 的責任）。
 *
 * ## 互推的優先規則
 *
 * 恆等式：`finish = addWorkingMinutes(calendar, start, durationMinutes)`。
 * 三項之中永遠有**兩項是輸入、一項是輸出**，由「使用者這次改了哪一項」決定：
 *
 * | 使用者改的欄位 | 固定不動        | 被重算            |
 * |----------------|-----------------|-------------------|
 * | `duration`     | `start`         | `finish`          |
 * | `finish`       | `start`         | `durationMinutes` |
 * | `start`        | `durationMinutes` | `finish`        |
 *
 * 特別注意「改開始日」那一列：**保持工期、推算新的完成日**，而不是「保持完成日、
 * 改工期」。把任務整段往後推是使用者改開始日時唯一符合直覺的意思；若改成保持
 * 完成日，往後拖一天就會無聲地把工期砍掉一天。
 *
 * ## 一律走日曆引擎
 *
 * 這裡**沒有任何 `Date` 運算**。所有時間位移都經 `addWorkingMinutes()` /
 * `workingMinutesBetween()` / `snapToNextWorkingTime()`，所以週末、國定假日、
 * 任務專屬日曆、非標準班別全部自動成立。
 *
 * ## 正規化
 *
 * - 工期 > 0 時 `start` 先對齊到下一個工作時刻（使用者可能輸入週六）。
 * - 改完成日時，先用 `workingMinutesBetween(start, finish)` 求得工期，再用
 *   `addWorkingMinutes(start, 工期)` 反算回**正規化後**的完成日。這保證
 *   「輸入非工作時間的完成日」會落回最近的合法工作時刻，且改完之後再讀一次
 *   工期會得到同一個值（round-trip 穩定）。
 *
 * ## 摘要任務
 *
 * 摘要任務的日期依定義由子任務 rollup 而來，直接編輯會在下一次排程被覆蓋，
 * 是「看起來成功、其實無效」的操作——因此一律**擋下**並回傳 `ok: false`，
 * 由呼叫端回報使用者。
 */

// ---------------------------------------------------------------------------
// 契約
// ---------------------------------------------------------------------------

/** 使用者這次編輯的欄位與新值。 */
export type TaskScheduleEdit =
  | { readonly kind: 'duration'; readonly durationMinutes: Minutes }
  | { readonly kind: 'start'; readonly start: ISODateTime }
  | { readonly kind: 'finish'; readonly finish: ISODateTime };

/** 被擋下的原因。 */
export type TaskScheduleEditRejection =
  /** `taskId` 不在專案裡。 */
  | 'TASK_NOT_FOUND'
  /** 摘要任務的日期由子任務彙總，不可直接編輯。 */
  | 'SUMMARY_DATES_ARE_ROLLED_UP'
  /** 工期為負數／非有限值。 */
  | 'INVALID_DURATION'
  /** 完成日早於開始日。 */
  | 'FINISH_BEFORE_START';

/** 互推結果要套到任務上的欄位（其餘欄位不動，CPM 欄位留給 `schedule()`）。 */
export type TaskSchedulePatch = Pick<
  Task,
  'start' | 'finish' | 'durationMinutes' | 'work' | 'isMilestone'
>;

export type TaskScheduleEditResult =
  | { readonly ok: true; readonly patch: TaskSchedulePatch }
  | {
      readonly ok: false;
      readonly reason: TaskScheduleEditRejection;
      readonly message: string;
    };

const reject = (
  reason: TaskScheduleEditRejection,
  message: string,
): TaskScheduleEditResult => ({ ok: false, reason, message });

// ---------------------------------------------------------------------------
// 實作
// ---------------------------------------------------------------------------

function calendarForTask(project: Project, task: Task): ResolvedCalendar {
  const calendarsById = new Map<string, Calendar>();
  for (const calendar of project.calendars) calendarsById.set(calendar.id as string, calendar);
  const id = (task.calendarId ?? project.defaultCalendarId) as string;
  const calendar = calendarsById.get(id) ?? calendarsById.get(project.defaultCalendarId as string);
  if (calendar === undefined) {
    throw new Error(`task-edit: calendar ${id} not found in project`);
  }
  const taskCalendar = resolveCalendar(calendar, calendarsById);
  if (task.ignoreResourceCalendar) return taskCalendar;
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);
  const calendars: ResolvedCalendar[] = [taskCalendar];
  for (const assignment of project.assignments) {
    if (assignment.taskId !== task.id) continue;
    const resource = resourcesById.get(assignment.resourceId as string);
    if (resource?.type !== 'Work') continue;
    const resourceId = (resource.calendarId ?? project.defaultCalendarId) as string;
    const resourceCalendar = calendarsById.get(resourceId);
    if (resourceCalendar === undefined) throw new Error(`task-edit: calendar ${resourceId} not found in project`);
    calendars.push(resolveCalendar(resourceCalendar, calendarsById));
  }
  return intersectWorkingCalendars(calendars);
}

/**
 * 依 `taskType` / `effortDriven` 由新工期重算工時。
 *
 * 重用 `recalculateEffort()`（工期 / 工時 / 單位三者的唯一鎖定關係實作），
 * `units` 取該任務所有指派的合計投入單位。**沒有任何指派時合計為 0**，
 * `recalculateEffort()` 會原樣回傳輸入——亦即工時維持不變。這是刻意的：
 * 沒有資源就沒有「work = duration × units」可解，硬把工時歸零只會在使用者
 * 之後指派資源時憑空多出一次無法解釋的變動。
 */
function workForDuration(project: Project, task: Task, durationMinutes: Minutes): Minutes {
  let unitSum = 0;
  for (const assignment of project.assignments) {
    if (assignment.taskId === task.id) unitSum += assignment.units as number;
  }
  const effort = recalculateEffort(
    task.taskType,
    task.effortDriven,
    { durationMinutes, work: task.work, units: makeUnits(unitSum) },
    'duration',
  );
  return effort.work;
}

/**
 * 計算一次「工期／開始日／完成日」編輯的結果。
 *
 * 不變更輸入的 `project`；呼叫端負責把 `patch` 套回任務並觸發重排。
 */
export function computeTaskScheduleEdit(
  project: Project,
  taskId: TaskId,
  edit: TaskScheduleEdit,
): TaskScheduleEditResult {
  const task = project.tasks.find((t) => t.id === taskId);
  if (task === undefined) {
    return reject('TASK_NOT_FOUND', `找不到任務 ${taskId as string}。`);
  }

  // `isSummary` 是推導欄位，可能在結構編輯的中間狀態下還沒收斂——因此
  // 同時看「有沒有子任務」這個第一手事實，兩者任一成立就擋下。
  const hasChildren = project.tasks.some((t) => t.parentId === taskId);
  if (task.isSummary || hasChildren) {
    return reject(
      'SUMMARY_DATES_ARE_ROLLED_UP',
      `「${task.name}」是摘要任務，日期與工期由子任務彙總，請改子任務。`,
    );
  }

  const calendar = calendarForTask(project, task);

  switch (edit.kind) {
    case 'duration': {
      const raw = edit.durationMinutes as number;
      if (!Number.isFinite(raw) || raw < 0) {
        return reject('INVALID_DURATION', '工期必須是 0 或正數。');
      }
      const duration = minutes(raw);
      // 工期 0（里程碑）不位移，開始日原樣保留；工期 > 0 才把起點對齊工作時刻。
      const start = raw > 0 ? snapToNextWorkingTime(calendar, task.start) : task.start;
      const finish = addWorkingMinutes(calendar, start, duration);
      return {
        ok: true,
        patch: {
          start,
          finish,
          durationMinutes: duration,
          work: workForDuration(project, task, duration),
          isMilestone: raw === 0,
        },
      };
    }

    case 'finish': {
      const start = snapToNextWorkingTime(calendar, task.start);
      const spanned = workingMinutesBetween(calendar, start, edit.finish) as number;
      if (spanned < 0) {
        return reject('FINISH_BEFORE_START', '完成日不可早於開始日。');
      }
      const duration = minutes(spanned);
      // 反算回正規化的完成日：使用者可能輸入週六或非工作時刻。
      const finish = addWorkingMinutes(calendar, start, duration);
      return {
        ok: true,
        patch: {
          start,
          finish,
          durationMinutes: duration,
          work: workForDuration(project, task, duration),
          isMilestone: spanned === 0,
        },
      };
    }

    case 'start': {
      const duration = task.durationMinutes;
      const isMilestone = (duration as number) === 0;
      const start = isMilestone ? edit.start : snapToNextWorkingTime(calendar, edit.start);
      const finish = addWorkingMinutes(calendar, start, duration);
      // 工期不變 → 工時也不變。
      return {
        ok: true,
        patch: {
          start,
          finish,
          durationMinutes: duration,
          work: task.work,
          isMilestone: task.isMilestone,
        },
      };
    }

    default: {
      const exhaustive: never = edit;
      throw new Error(`computeTaskScheduleEdit(): unknown edit ${JSON.stringify(exhaustive)}`);
    }
  }
}
